export const RETENTION_UNITS = ["days", "years"] as const;
export type RetentionUnit = (typeof RETENTION_UNITS)[number];

export type RetentionFormValues = {
  retention_unit: RetentionUnit;
  retention_value_years: number | null;
  retention_value_days: number | null;
};

export const DAYS_PER_YEAR = 365;
export const MIN_DAYS = 90;
export const MAX_DAYS = 1095;

/**
 * Determines the initial form values for data retention based on a days value.
 * If the days value is a multiple of 365, uses years; otherwise uses days.
 * If days is null/undefined, defaults to MAX_DAYS.
 */
export function getInitialFormValues({
  days,
}: {
  days: number | null | undefined;
}): RetentionFormValues {
  const daysValue = days ?? MAX_DAYS;

  if (daysValue % DAYS_PER_YEAR === 0) {
    return {
      retention_unit: "years",
      retention_value_years: daysValue / DAYS_PER_YEAR,
      retention_value_days: null,
    };
  }

  return {
    retention_unit: "days",
    retention_value_years: null,
    retention_value_days: daysValue,
  };
}

/**
 * Converts a retention value from one unit to another.
 * When converting days to years, only converts if it's an exact year (divisible by 365).
 * Returns null if conversion is not exact or if the resulting days value exceeds MAX_DAYS.
 */
export function convertValueToUnit({
  value,
  fromUnit,
  toUnit,
}: {
  value: number | null | undefined;
  fromUnit: RetentionUnit;
  toUnit: RetentionUnit;
}): number | null | undefined {
  if (!value) return value;

  const daysValue = fromUnit === "years" ? value * DAYS_PER_YEAR : value;
  if (daysValue > MAX_DAYS) return null;

  if (fromUnit === toUnit) return value;

  if (fromUnit === "years" && toUnit === "days") {
    return daysValue;
  }

  if (fromUnit === "days" && toUnit === "years") {
    if (value % DAYS_PER_YEAR === 0) {
      return value / DAYS_PER_YEAR;
    }
    return null;
  }

  return null;
}
